import React, { Fragment, useEffect, useState } from 'react';
import Header from '../../components/Header';
import Load from '../../components/Load';
import AdminUsuarioTable from '../../components/admin/AdminUsuarioTable';
import AdminUsuarioModal from '../../components/admin/AdminUsuarioModal';
import ChangePasswordDialog from '../../components/admin/athletes/ChangePasswordDialog';
import { listarUsuariosAdmin, Usuario } from '../../services/usuarioService';
import { Snackbar, Alert } from '@mui/material';

const AdminUsuariosPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState<Usuario | null>(null);

  // Password dialog state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordDialogUser, setPasswordDialogUser] = useState<Usuario | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  // Filter state
  const [nomeFilter, setNomeFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'true' | 'false'>('');

  const fetchUsuarios = async (nextPage: number = page, nextSize: number = rowsPerPage) => {
    try {
      setLoading(true);
      setError(null);
      const params: {
        page: number;
        size: number;
        nome?: string;
        email?: string;
        ativo?: boolean;
      } = {
        page: nextPage,
        size: nextSize,
        nome: nomeFilter || undefined,
        email: emailFilter || undefined,
      };
      if (statusFilter === 'true') params.ativo = true;
      if (statusFilter === 'false') params.ativo = false;

      const connection = await listarUsuariosAdmin(params);
      setUsuarios(connection.items ?? []);
      setTotal(connection.totalItems ?? 0);
    } catch {
      setError('Erro ao carregar usuários.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, [page, rowsPerPage]);

  const handlePageChange = (p: number, size: number) => {
    setPage(p);
    setRowsPerPage(size);
  };

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (page !== 0) {
      setPage(0);
    } else {
      fetchUsuarios(0, rowsPerPage);
    }
  };

  const handleNew = () => {
    setFeedback(null);
    setSelectedUsuario(null);
    setModalOpen(true);
  };

  const handleEdit = (usuario: Usuario) => {
    setFeedback(null);
    setSelectedUsuario(usuario);
    setModalOpen(true);
  };

  const handleSaved = () => {
    setModalOpen(false);
    setFeedback(selectedUsuario ? 'Admin atualizado com sucesso.' : 'Admin criado com sucesso.');
    setSelectedUsuario(null);
    fetchUsuarios();
  };

  const handleChangePassword = (usuario: Usuario) => {
    setPasswordDialogUser(usuario);
    setPasswordDialogOpen(true);
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setPasswordDialogUser(null);
  };

  const handlePasswordChanged = () => {
    setPasswordDialogOpen(false);
    setPasswordDialogUser(null);
    setSnackbar({ open: true, message: 'Senha alterada com sucesso!' });
  };

  return (
    <Fragment>
      <div className="main-wrapper">
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="middle-wrap">
                <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                </div>
                <div className="card w-100 border-0 bg-white shadow-xs p-3 mb-3">
                  <div className="card-body p-4 w-100 bg-white border-0 d-flex rounded-3">
                    <button
                      className="d-inline-block mt-2 me-2 btn btn-link text-white p-0"
                      onClick={() => window.history.back()}
                    >
                      <i className="ti-arrow-left font-sm" />
                    </button>
                    <h4 className="font-xs text-primary fw-600 ms-2 mb-0 mt-2 flex-grow-1">
                      ← Gerenciar Admins
                    </h4>
                    <span className="text-primary mt-2">Total: {total}</span>
                  </div>
                  <form onSubmit={handleFilterSubmit} className="mb-3">
                    <div className="row g-2 align-items-end">
                      <div className="col-md-3">
                        <label htmlFor="nomeFilter" className="form-label">Nome</label>
                        <input
                          id="nomeFilter"
                          type="text"
                          className="form-control form-control-sm"
                          value={nomeFilter}
                          onChange={(e) => setNomeFilter(e.target.value)}
                          placeholder="Buscar por nome"
                        />
                      </div>
                      <div className="col-md-3">
                        <label htmlFor="emailFilter" className="form-label">Email</label>
                        <input
                          id="emailFilter"
                          type="text"
                          className="form-control form-control-sm"
                          value={emailFilter}
                          onChange={(e) => setEmailFilter(e.target.value)}
                          placeholder="Buscar por email"
                        />
                      </div>
                      <div className="col-md-3">
                        <label htmlFor="statusFilter" className="form-label">Status</label>
                        <select
                          id="statusFilter"
                          className="form-select form-select-lg"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value as '' | 'true' | 'false')}
                        >
                          <option value="">Todos</option>
                          <option value="true">Ativo</option>
                          <option value="false">Inativo</option>
                        </select>
                      </div>
                      <div className="col-md-3 d-grid">
                        <button type="submit" className="btn btn-primary mb-1">Filtrar</button>
                        <button type="button" className="btn btn-primary" onClick={handleNew}>
                          <i className="bi bi-plus me-1" /> Novo Admin
                        </button>
                      </div>
                    </div>
                  </form>
                  {loading ? (
                    <Load />
                  ) : error ? (
                    <div className="alert alert-danger">{error}</div>
                  ) : usuarios.length === 0 ? (
                    <div className="alert alert-info">Nenhum usuário encontrado.</div>
                  ) : (
                    <>
                      {feedback && (
                        <div className="alert alert-success" role="alert">
                          {feedback}
                        </div>
                      )}
                      <AdminUsuarioTable
                        usuarios={usuarios}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        total={total}
                        onPageChange={handlePageChange}
                        onEdit={handleEdit}
                        onChangePassword={handleChangePassword}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <AdminUsuarioModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          usuario={selectedUsuario || undefined}
          onSaved={handleSaved}
        />
        {passwordDialogUser && (
          <ChangePasswordDialog
            open={passwordDialogOpen}
            onClose={handlePasswordDialogClose}
            onSuccess={handlePasswordChanged}
            userId={passwordDialogUser.id}
            userName={passwordDialogUser.nome}
          />
        )}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ open: false, message: '' })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setSnackbar({ open: false, message: '' })} severity="success" sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </Fragment>
  );
};

export default AdminUsuariosPage;
